import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { apiFetch } from '../../context/AppContext';
import { Table, DashboardStatCard } from '../../components/common/UI';
import {
  Building2, Users, Package, TrendingUp, ArrowRight, Activity,
  Shield, Globe, BarChart3, Clock, CheckCircle2, AlertCircle,
  PlusCircle, Eye, Settings, ChevronRight, Layers, Mail,
} from 'lucide-react';

export default function AdminDashboard() {
  const { showNotification } = useApp();

  const [stats, setStats]     = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Fetch dashboard stats ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statsRes, tenantsRes] = await Promise.all([
          apiFetch('/admin/dashboard'),
          apiFetch('/admin/tenants?page=1&page_size=5'),
        ]);

        if (!statsRes.ok || !tenantsRes.ok) {
          showNotification('Failed to load dashboard data', 'error');
          return;
        }

        const statsData   = await statsRes.json();
        const tenantsData = await tenantsRes.json();

        if (!cancelled) {
          setStats(statsData);
          setTenants(tenantsData.items || []);
        }
      } catch (err) {
        if (!cancelled) showNotification('Network error loading dashboard', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showNotification]);

  // ── Stat cards ──────────────────────────────────────────────────────────
  const statCards = stats
    ? [
        {
          label: 'Total Tenants',
          tooltip: 'Customer organisations on the platform, and how many are currently active.',
          value: stats.total_tenants,
          sub: `${stats.active_tenants} active`,
          icon: Building2,
          color: 'from-indigo-500 to-violet-600',
        },
        {
          label: 'Total Users',
          tooltip: 'User accounts across every organisation on the platform.',
          value: stats.total_users_across_tenants,
          sub: 'across tenants',
          icon: Users,
          color: 'from-emerald-400 to-teal-600',
        },
        {
          label: 'Licenses',
          tooltip: 'User seats that have been allocated to organisations.',
          value: stats.total_licenses_allocated,
          sub: 'allocated',
          icon: Package,
          color: 'from-purple-500 to-indigo-600',
        },
        {
          label: 'System',
          tooltip: 'Platform availability for the current month.',
          value: '99.9%',
          sub: 'uptime this month',
          icon: Activity,
          color: 'from-amber-400 to-orange-500',
        },
      ]
    : [];

  // ── Table columns (mirrors real TenantListOut shape) ───────────────────
  const columns = [
    {
      key: 'company_name',
      label: 'Organization',
      render: (v, row) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md">
            <Building2 size={16} className="text-white" />
          </div>
          <div>
            <div className="font-semibold text-slate-800 text-sm">{v}</div>
            {row.domain_name && (
              <div className="text-xs text-slate-400 flex items-center gap-1">
                <Globe size={10} />
                {row.domain_name}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (v) => (
        <span
          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
            ${v === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}
        >
          {v === 'active' ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
          {v}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (v) => (
        <span className="flex items-center gap-1 text-xs text-slate-400">
          <Clock size={12} />
          {new Date(v).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'id',
      label: '',
      render: () => (
        <Link
          to="/admin/tenants"
          className="flex items-center gap-1 text-indigo-600 text-xs font-medium hover:underline"
        >
          <Eye size={12} />
          View
        </Link>
      ),
    },
  ];

  // ── Quick actions ───────────────────────────────────────────────────────
  const QUICK_ACTIONS = [
    { icon: PlusCircle, label: 'Add Tenant',    color: 'text-indigo-600', to: '/admin/tenants'  },
    { icon: Users,      label: 'Manage Users',  color: 'text-emerald-600', to: '/admin/tenants'  },
    { icon: Package,    label: 'Licenses',      color: 'text-purple-600', to: '/admin/licenses' },
    { icon: Settings,   label: 'Settings',      color: 'text-amber-600',  to: '/admin/settings' },
    { icon: Mail,       label: 'Email Template', color: 'text-rose-500',   to: '/admin/email-templates' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
        Loading dashboard…
      </div>
    );
  }

  return (
    <div className="p-1 space-y-6 min-h-screen">

      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg">
            <Layers size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-sm text-slate-400 flex items-center gap-1">
              <Globe size={12} />
              Platform overview &amp; analytics
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-emerald-50 px-3 py-1.5 rounded-full text-xs text-emerald-600">
          <CheckCircle2 size={12} />
          System Healthy
        </div>
      </div>

      {/* STATS */}
      <div data-tour-id="tour-admin-dashboard" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {(loading && !stats
          ? [
              { label: 'Total Tenants', tooltip: 'Customer organisations on the platform, and how many are currently active.', icon: Building2, iconClass: 'from-indigo-500 to-violet-600' },
              { label: 'Total Users', tooltip: 'User accounts across every organisation on the platform.', icon: Users, iconClass: 'from-emerald-400 to-teal-600' },
              { label: 'Licenses', tooltip: 'User seats that have been allocated to organisations.', icon: Package, iconClass: 'from-purple-500 to-indigo-600' },
              { label: 'System', tooltip: 'Platform availability for the current month.', icon: Activity, iconClass: 'from-amber-400 to-orange-500' },
            ]
          : statCards
        ).map((s, i) => (
          <DashboardStatCard
            key={i}
            label={s.label}
            tooltip={s.tooltip}
            value={s.value}
            sub={s.sub}
            icon={s.icon}
            iconClass={s.color || s.iconClass}
            loading={loading && !stats}
            subIcon={<TrendingUp size={10} />}
          />
        ))}
      </div>

      {/* QUICK ACTIONS */}
      <div data-tour-id="tour-admin-quick-actions" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {QUICK_ACTIONS.map((a, i) => {
          const Icon = a.icon;
          return (
            <Link
              key={i}
              to={a.to}
              className="bg-white border rounded-xl p-4 hover:shadow-md transition flex items-center gap-3"
            >
              <Icon className={a.color} />
              <span className="text-sm font-medium">{a.label}</span>
              <ChevronRight className="ml-auto text-slate-300" />
            </Link>
          );
        })}
      </div>

      {/* TENANT TABLE */}
      <div data-tour-id="tour-admin-tenant-table" className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-indigo-500" />
            <h2 className="text-sm font-semibold">Recent Tenants</h2>
          </div>
          <Link to="/admin/tenants" className="text-indigo-600 text-sm flex items-center gap-1">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="p-4">
          <Table columns={columns} data={tenants} />
        </div>
      </div>

      {/* SECURITY BADGE */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-5 flex items-center gap-3">
        <Shield className="text-indigo-600" />
        <div>
          <div className="font-semibold text-slate-800">Enterprise Security</div>
          <div className="text-xs text-slate-500">SOC2 · ISO 27001 · GDPR</div>
        </div>
      </div>

    </div>
  );
}